import { hasPermission } from "../../../../../app/auth.js";
import { currentView } from "../../../../../app/viewManager.js";

const appInputTypes = new Set(['dropdown', 'text', 'textarea', 'datepicker', 'daterange', 'timerange', 'timepicker']);

function isVisible(config) {
    if (!config) return false;
    const viewMatch = !config.requiredView || config.requiredView == currentView;
    const permMatch = !config.permission || hasPermission(config.permission);
    return viewMatch && permMatch;
}

export function createElement(config) {
    if (!config || !isVisible(config)) return null;
    if (config instanceof HTMLElement) return config;
    const {
        type = "p", text, children, attributes, onclick, onchange, options, selectedValue,
        class: className, onMount, value, checked, multiple, placeholder, style: styleObj,
        label, required, onceOptional, permission, requiredView, minHeight,
        ...rest
    } = config;

    const isAppInput = appInputTypes.has(type);
    const element = isAppInput ? document.createElement('app-input') : document.createElement(type);

    if (isAppInput) {
        element.setAttribute('type', type);
        if (type === 'dropdown' && !multiple) element.setAttribute('empty-option', '');
        if (multiple)    element.setAttribute('multiple', '');
        if (placeholder) element.setAttribute('placeholder', placeholder);
    }

    Object.assign(element, rest);
    if (className)                          element.className = className;
    if (text !== undefined)                 element.innerText = text;
    if (onclick)                            element.onclick = onclick;
    if (onchange)                           element.onchange = event => onchange(event.target.value);
    if (!isAppInput && value !== undefined) element.value = value;
    if (checked !== undefined)              element.checked = checked;
    if (styleObj) {
        Object.entries(styleObj).forEach(([key, val]) => {
            if (key.startsWith('--')) element.style.setProperty(key, val);
            else element.style[key] = val;
        });
    }
    if (required)                           element.setAttribute('data-required', '');

    if (attributes) Object.entries(attributes).forEach(([key, val]) => element.setAttribute(key, val));

    if (options) {
        options.forEach(({ id, name }) => element.appendChild(createElement({ type: "option", text: name, attributes: { value: id } })));
        if (selectedValue !== undefined) element.value = selectedValue;
    }

    if (Array.isArray(children)) {
        children.forEach(child => {
            const builtChild = createElement(child);
            if (builtChild) element.appendChild(builtChild);
        });
    }

    if (typeof onMount === "function") setTimeout(() => onMount(element), 0);
    return element;
}

const patternHelper = {
    label: (field, className = field.labelClass) => ({
        type: "p",
        class: className,
        requiredView: field.requiredView,
        permission: field.permission,
        ...(field.required || field.onceOptional
            ? { children: [{ type: "span", text: field.label }, { type: "span", text: field.required ? " *" : " ~", class: field.required ? "requiredMark" : "onceOptionalMark" }] }
            : { text: field.label }
        )
    }),

    field: (field) => {
        const type = field.type || "dropdown";
        const minHeightStyle = !field.minHeight ? {} : appInputTypes.has(type)
            ? { '--inputTextareaMinHeight': field.minHeight, '--inputTriggerMultiMinHeight': field.minHeight }
            : { minHeight: field.minHeight };

        return {
            ...field,
            type,
            class: field.class || "dialogField dialogInput",
            style: field.minHeight ? { ...minHeightStyle, ...field.style } : field.style
        };
    }
};

const patterns = {
    grid: (fields, columns) => {
        const visibleFields = fields.filter(field => isVisible(field));
        if (visibleFields.length === 0) return [];
        return [{
            type: "div",
            class: "dialogGrid selectRow",
            style: columns ? { gridTemplateColumns: columns } : undefined,
            children: visibleFields.map(field => ({
                type: "div",
                class: "fieldGroup",
                children: [
                    ...(field.label ? [patternHelper.label(field, "fieldLabel")] : []),
                    patternHelper.field(field)
                ]
            }))
        }];
    },

    verticalGrid: (rows, _columns, align) => {
        const builtRows = rows.flatMap(row => patterns.grid(row.fields, row.columns || row.width));
        if (builtRows.length === 0) return [];

        return [{
            type: "div",
            class: align === "center" ? "verticalGridGroup verticalGridGroup-center" : "verticalGridGroup",
            children: builtRows
        }];
    },

    message: texts => texts.map(text => ({
        type: "p",
        text,
        class: "message"
    })),

    footer: buttons => {
        const visibleButtons = buttons.filter(button => isVisible(button));
        if (visibleButtons.length === 0) return [];

        return [{
            type: "div",
            class: "footerRow",
            children: visibleButtons.map(button => ({
                ...button,
                type: "button",
                class: button.class || "bigButton"
            }))
        }];
    }
};

const editingFieldsByItem = new WeakMap();
function editingFieldsOf(item) {
    let fields = editingFieldsByItem.get(item);
    if (!fields) { fields = new Set(); editingFieldsByItem.set(item, fields); }
    return fields;
}

export function renderItemCards(list, items, row, draftItem = null) {
    if (!list) return;
    list.innerHTML = '';

    const toggleBtn = list.closest('.appManager')?.querySelector('.appManagerAddToggleBtn');
    if (toggleBtn) toggleBtn.classList.toggle('hidden', draftItem != null);

    if (items.length === 0) {
        list.appendChild(createElement({ type: "p", class: "appManagerEmpty", text: row.emptyText }));
        return;
    }

    const readFieldValue = el => (typeof el.getValue === 'function' ? el.getValue() : el.value);
    const rerender = () => renderItemCards(list, items, row, draftItem);

    items.forEach(item => {
        const confirmed = item !== draftItem;
        const editingFields = editingFieldsOf(item);

        const infoChildren = (row.fields ?? []).map((field, index) => {
            const cssClass = index === 0 ? "appManagerItemName" : "appManagerItemUrl";
            const editing = !confirmed || (field.editable === "always" && editingFields.has(field.key));
            if (!editing) {
                return createElement({ type: "span", class: cssClass, text: item[field.displayKey ?? field.key] ?? "" });
            }
            const isDropdown = field.render === "dropdown";
            const input = createElement({
                type: isDropdown ? "dropdown" : "text",
                class: field.class || "dialogInput",
                placeholder: field.placeholder,
                attributes: { "data-item-field": field.key },
            });
            if (isDropdown) input.setOptions(row.getFieldOptions?.(field, item) ?? []);
            input.setValue(item[field.key] ?? (isDropdown ? null : ""), { silent: true });
            return input;
        });

        const buttons = [];
        if (!confirmed) {
            const confirmBtn = createElement({
                type: "button", class: "appManagerEditBtn", attributes: { type: "button", title: "Speichern" },
                children: [{ type: "i", class: "fa-solid fa-check" }]
            });
            confirmBtn.addEventListener('click', async () => {
                const card = confirmBtn.closest('.appManagerItem');
                const values = {};
                card.querySelectorAll('[data-item-field]').forEach(el => { values[el.dataset.itemField] = readFieldValue(el); });
                await row.onConfirm?.(item, values);
            });
            buttons.push(confirmBtn);
        } else {
            (row.fields ?? []).filter(field => field.editable === "always").forEach(field => {
                const isEditing = editingFields.has(field.key);
                const btn = createElement({
                    type: "button", class: "appManagerEditBtn",
                    attributes: { type: "button", title: isEditing ? "Speichern" : "Bearbeiten" },
                    children: [{ type: "i", class: isEditing ? "fa-solid fa-check" : "fa-solid fa-pen" }]
                });
                btn.addEventListener('click', () => {
                    if (isEditing) {
                        const el = btn.closest('.appManagerItem').querySelector(`[data-item-field="${field.key}"]`);
                        item[field.key] = String(readFieldValue(el) ?? "").trim();
                        editingFields.delete(field.key);
                    } else {
                        editingFields.add(field.key);
                    }
                    rerender();
                });
                buttons.push(btn);
            });
        }

        (row.buttons ?? []).forEach(entry => {
            if (entry.type === "remove") {
                const removeBtn = createElement({
                    type: "button", class: "appManagerRemoveBtn", attributes: { type: "button" },
                    children: [{ type: "i", class: "fa-solid fa-xmark" }]
                });
                removeBtn.addEventListener('click', () => row.onRemove?.(item));
                buttons.push(removeBtn);
                return;
            }
            if (!confirmed) return;
            if (entry.visible && !entry.visible(item, confirmed)) return;

            const title = typeof entry.title === "function" ? entry.title(item) : entry.title;
            if (entry.type === "toggle") {
                const btn = createElement({
                    type: "button", class: `${entry.class || "appManagerToggleBtn"}${item[entry.field] ? " active" : ""}`,
                    attributes: { type: "button", title },
                    children: [{ type: "i", class: entry.icon }]
                });
                btn.addEventListener('click', () => { entry.onClick?.(item); rerender(); });
                buttons.push(btn);
            } else if (entry.type === "action") {
                const btn = createElement({
                    type: "button", class: entry.class || "appManagerActionBtn", attributes: { type: "button", title },
                    children: [{ type: "i", class: entry.icon }]
                });
                btn.addEventListener('click', () => entry.onClick?.(item));
                buttons.push(btn);
            }
        });

        const card = createElement({
            type: "div", class: "appManagerItem",
            children: [{ type: "div", class: "appManagerItemInfo", children: infoChildren }, ...buttons]
        });
        list.appendChild(card);
    });

    if (draftItem || items.some(item => editingFieldsByItem.get(item)?.size)) list.querySelector('.appManagerItem [data-item-field]')?.focus?.();
}

function buildItemManager(row) {
    const { listLabel, addLabel, onMount, onAddToggle } = row;

    const toggleBtn = createElement({
        type: "button", class: "appManagerAddToggleBtn", attributes: { type: "button" },
        children: [{ type: "i", class: "fa-solid fa-plus" }, { type: "span", text: addLabel }]
    });
    toggleBtn.addEventListener('click', () => onAddToggle?.());

    const children = [
        ...(listLabel ? [{ type: "p", class: "appManagerLabel", text: listLabel }] : []),
        { type: "div", class: "appManagerList" },
        toggleBtn,
    ];
    return createElement({ type: "div", class: "appManager", children, onMount: onMount ? (element) => onMount(element, row) : undefined });
}

function buildWizardStep(step, index) {
    const rowChildren = (step.rows || []).map(buildRow).filter(Boolean);
    return createElement({
        type: "div",
        class: `wizardStep${index === 0 ? " active" : ""}`,
        attributes: { "data-step": String(index) },
        children: rowChildren
    });
}

function buildWizardProgress(steps, onStepClick) {
    const children = steps.flatMap((step, index) => [
        {
            type: "div",
            class: `wizardProgressStep${index === 0 ? " active" : ""}${onStepClick ? " clickable" : ""}`,
            attributes: { "data-step": String(index) },
            onclick: onStepClick ? () => onStepClick(index) : undefined,
            children: [
                { type: "div", class: "wizardProgressCircle", text: String(index + 1) },
                { type: "p", class: "wizardProgressLabel", text: step.label },
            ]
        },
        ...(index < steps.length - 1 ? [{ type: "div", class: "wizardConnector" }] : [])
    ]);
    return createElement({ type: "div", class: "wizardProgress", children });
}

function buildWizard(row) {
    const steps = row.steps || [];
    const progress = buildWizardProgress(steps, row.onStepClick);
    const stepEls = steps.map((step, index) => buildWizardStep(step, index));
    return createElement({
        type: "div",
        class: "wizardContainer",
        children: [progress, { type: "div", class: "wizardSteps", children: stepEls }]
    });
}

function buildRow(row) {
    if (row instanceof HTMLElement) return row;
    if (!isVisible(row)) return null;

    if (row.pattern === 'divider')     return createElement({ type: "hr", class: `dialogDivider${row.size ? ` divider-${row.size}` : ''}` });
    if (row.pattern === 'itemManager') return buildItemManager(row);
    if (row.pattern === 'wizard')      return buildWizard(row);

    if (row.pattern && patterns[row.pattern]) {
        const patternElements = patterns[row.pattern](row.fields || row.buttons || row.texts || row.rows, row.columns, row.align).filter(Boolean);
        if (patternElements.length === 0) return null;
        const isGridRow = row.pattern === "grid" || row.pattern === "verticalGrid";
        const baseClass = row.class || (isGridRow ? "rowGroup" : "row");
        return createElement({
            type: "div",
            class: row.background ? `${baseClass} rowBackground` : baseClass,
            children: patternElements
        });
    }

    const rawChildren = row.type ? [row] : (row.elements || row.children || []);
    const visibleChildren = rawChildren.filter(c => isVisible(c));
    if (visibleChildren.length === 0) return null;
    return createElement({
        type: "div",
        class: row.class || "row",
        children: visibleChildren
    });
}

export function buildDialog(config = null) {
    if (!config?.content) return console.error("Konfiguration ungültig");

    const contentChildren = config.content.rows.map(buildRow).filter(Boolean);
    return createElement({ type: "div", class: "content", children: contentChildren });
}